import { type ReactNode, useEffect } from 'react'
import { X } from 'lucide-react'
import { useBodyScrollLock } from './useBodyScrollLock'

export interface ModalProps {
  show: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
}

const sizeClasses = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-3xl' }

/** Modal generico basato su useState show/hide, sostituisce sia i modali Bootstrap già React-based sia i vecchi data-bs-toggle="modal". */
export function Modal({ show, onClose, title, children, footer, size = 'md' }: ModalProps) {
  useBodyScrollLock(show)

  useEffect(() => {
    if (!show) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [show, onClose])

  if (!show) return null

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center overscroll-none p-4">
      <div className="absolute inset-0 bg-overlay/50" onClick={onClose} />
      <div data-popover-portal-host className="pointer-events-none fixed inset-0 z-[var(--z-index-tooltip)]" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative z-10 flex max-h-[90vh] w-full flex-col overflow-hidden overscroll-contain rounded-lg bg-canvas shadow-xl ${sizeClasses[size]}`}
      >
        {title && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3">
            {/* min-w-0 è necessario perché il figlio di un flex item di default non si
                restringe sotto la larghezza del suo contenuto (min-width: auto) - senza,
                un titolo lungo e senza spazi (es. un URL) spinge il contenitore invece di
                andare a capo, traboccando fuori dal modale. */}
            <h5 className="m-0 min-w-0 flex-1 text-base font-semibold wrap-break-word">{title}</h5>
            <button onClick={onClose} className="shrink-0 cursor-pointer text-muted hover:text-body" aria-label="Chiudi">
              <X size={18} />
            </button>
          </div>
        )}
        <div className="min-h-0 overflow-y-auto overscroll-contain p-4">{children}</div>
        {footer && <div className="flex shrink-0 justify-end gap-2 border-t border-border px-4 py-3">{footer}</div>}
      </div>
    </div>
  )
}
